import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { postLoginUser } from "@/services/postLoginUser";

import { normalizeAccount } from "../auth/utils";
import { useAuth } from "../hooks/useAuth";
import { selectAuthStatus, setAccount, setStatus } from "../store/authSlice";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchUserDetailsByEmail, getFluxxId, resetRoleState } from "../store/roleSlice";

export default function AuthInitializer() {
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const authStatus = useAppSelector(selectAuthStatus);
  const { postLogin } = useAuth();
  const { instance } = useMsal();

  // const [userId, setUserId] = React.useState<string | null>(null);

  //Prevent duplicate runs
  const hasInitializedRef = React.useRef(false);

  React.useEffect(() => {
    const run = async () => {
      //Handle logout case
      if (!isAuthenticated) {
        // console.log("[AuthInitializer] User signed out");

        dispatch(resetRoleState());
        dispatch(setStatus("signedOut"));
        dispatch(setAccount(null));

        // setUserId(null);
        hasInitializedRef.current = false;

        return;
      }

      //Prevent duplicate execution
      if (hasInitializedRef.current) return;

      hasInitializedRef.current = true; //lock immediately

      try {
        dispatch(setStatus("pending"));

        //Get active account safely
        let acc = instance.getActiveAccount();

        if (!acc) {
          const accounts = instance.getAllAccounts();
          if (accounts.length === 0) {
            console.warn("No MSAL accounts found");
            return;
          }

          acc = accounts[0];
          instance.setActiveAccount(acc);
        }

        const normalizedAcc = normalizeAccount(acc);
        dispatch(setAccount(normalizedAcc));

        // console.log("[AuthInitializer] Starting post-login bootstrap");

        // ✅ SHOULD succeed
        await postLogin();

        try {
          await postLoginUser(normalizedAcc.username);
        } catch (err) {
          console.error("[AuthInitializer] postLoginUser failed:", err);
        }

        try {
          await dispatch(getFluxxId(normalizedAcc.username));
        } catch (err) {
          console.warn("[AuthInitializer] getFluxxId failed:", err);
        }

        try {
          dispatch(fetchUserDetailsByEmail(normalizedAcc.username));
        } catch (err) {
          console.warn("[AuthInitializer] fetchUserDetails failed:", err);
        }

        //Extract userId safely
        // const extractedUserId =
        //   (normalizedAcc as any)?.id || (acc as any)?.homeAccountId || (acc as any)?.localAccountId;

        // if (extractedUserId) {
        //   setUserId(extractedUserId);
        // }

        //Mark success
        dispatch(setStatus("signedIn"));

        // console.log("[AuthInitializer] Login bootstrap complete");

        //Navigate once
        if (location.pathname === "/login" || location.pathname === "/") {
          navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        console.error("[AuthInitializer] Critical failure:", err);
        dispatch(setStatus("signedIn"));
      }
    };

    void run();
  }, [isAuthenticated, postLogin, instance, dispatch, navigate, location, authStatus]);

  return null;
}
